import { useCurrentUser } from "@src/context/UserContext";
import { isLocalDevelopment, VITE_OVERRIDE_ACCESS_LEVELS } from "@src/env";
import { useFeatureFlag } from "@src/backpack-console/featureFlags";
import { ParsedToken } from "firebase/auth";
import { atom, useAtomValue } from "jotai";
import { useMemo } from "react";
import { useRouteMatch } from "react-router-dom";

export const ROLE_BASED_PERMISSIONS_FEATURES = [
  "Building Overview",
  "Utilities",
  "Property",
  "Submissions",
  "Documents",
  "Operations",
  "Equipment",
  "Projects",
  "Rebates",
  "User Management",
] as const;

export type RoleBasedPermissionsFeature =
  (typeof ROLE_BASED_PERMISSIONS_FEATURES)[number];

function hasProperty<T extends Record<string, unknown>, K extends string>(
  obj: T,
  key: K
): obj is T & Record<K, unknown> {
  return key in obj;
}

const ACCESS_LEVELS = {
  read: "read",
  edit: "edit",
} as const;

export type AccessLevel = keyof typeof ACCESS_LEVELS;

export type PermissionsClaims = {
  feature: RoleBasedPermissionsFeature;
  access: AccessLevel;
}[];

export const permissionsClaimsOverridesAtom = atom<PermissionsClaims>([]);

const useOverridePermissionsClaims = () => {
  const overrides = useAtomValue(permissionsClaimsOverridesAtom);
  if (!isLocalDevelopment || !VITE_OVERRIDE_ACCESS_LEVELS) return null;
  return overrides;
};

export function getParsedTokenPermissions(
  claims?: ParsedToken,
  opts?: {
    isRoleBasedPermissionsFlagEnabled: boolean;
  }
) {
  if (
    !opts?.isRoleBasedPermissionsFlagEnabled ||
    !claims ||
    !hasProperty(claims, "permissions")
  ) {
    return null;
  }
  return claims.permissions as PermissionsClaims;
}

/**
 * Returns permissions claims (currentUser.permissions) after
 * check for feature flag and overrides.
 */
export function useAllPermissionsClaimsAccessLevels(): PermissionsClaims {
  const { permissions, superAdmin } = useCurrentUser();

  const printRouteMatches = !!useRouteMatch("/print");

  const overrides = useOverridePermissionsClaims();

  const isRoleBasedPermissionsFlagEnabled =
    useFeatureFlag("role-based-permissions", "off") === "v1";

  return useMemo(() => {
    // if the flag is off for the user, return edit access (current capability)
    // super admins can edit for every feature
    if (!isRoleBasedPermissionsFlagEnabled || superAdmin) {
      return ROLE_BASED_PERMISSIONS_FEATURES.map((feature) => ({
        feature,
        access: "edit",
      }));
    }

    // PDF service uses unauthenticated print routes without active session - allow read access
    if (printRouteMatches) {
      return ROLE_BASED_PERMISSIONS_FEATURES.map((feature) => ({
        feature,
        access: "read",
      }));
    }

    if (overrides) return overrides;

    // if permissions are missing from the claims, something has gone wrong
    if (!permissions) {
      return [];
    }

    return permissions;
  }, [
    isRoleBasedPermissionsFlagEnabled,
    overrides,
    permissions,
    printRouteMatches,
    superAdmin,
  ]);
}
